import React, { useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Trans, t } from '@lingui/macro';
import { Mic, Pause, Play, Loader2, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useSpeechToText } from '../../hooks/useSpeechToText';
import { My } from '../../services/My';
import { Fire, getFunc } from '../../services/FireClient';
import { analyzeMinutes } from '../../services/AiMemoAnalyzer';
import { cn } from '../../lib/utils';
import { Capacitor } from '@capacitor/core';
import VoiceWaveform from './VoiceWaveform';
import { ref, uploadBytes } from 'firebase/storage';
import { getStorage } from 'firebase/storage';
import { KeepDeviceAwake } from '../../services/Utils/KeepDeviceAwake';
import { IconSparkles } from '../components/FigmaIcons';

interface AiMinutesCreateProps {
    roomId: string;
    onSaved: (id: string) => void;
    onCancel: () => void;
    onViewMinutes?: (id: string) => void;
}

const LANG_OPTIONS = [
    { code: 'ko-KR', label: '한국어', flag: '🇰🇷' },
    { code: 'ja-JP', label: '日本語', flag: '🇯🇵' },
    { code: 'en-US', label: 'English', flag: '🇺🇸' },
];

const AiMinutesCreate: React.FC<AiMinutesCreateProps> = ({ roomId, onSaved, onCancel, onViewMinutes }) => {
    const defaultLang = My.user?.lang === 'ja' ? 'ja-JP' : My.user?.lang === 'en' ? 'en-US' : 'ko-KR';
    const [sttLang, setSttLang] = useState(defaultLang);
    // Android 네이티브에서는 실시간 STT 비활성화 (배치 고정밀 STT만 사용)
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [savedTranscript, setSavedTranscript] = useState('');
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [timerInterval, setTimerInterval] = useState<ReturnType<typeof setInterval> | null>(null);
    const [sttUpgradeStatus, setSttUpgradeStatus] = useState<'idle' | 'uploading' | 'processing' | 'done' | 'upload-error' | 'stt-error'>('idle');
    const [savedMinutesId, setSavedMinutesId] = useState<string | null>(null);
    const [aiAnalysisStatus, setAiAnalysisStatus] = useState<'idle' | 'analyzing' | 'done' | 'error'>('idle');


    // MediaRecorder for audio capture
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const audioMimeTypeRef = useRef<string>('audio/webm');
    const audioContextRef = useRef<AudioContext | null>(null);
    const lastSttTranscriptRef = useRef<string>('');

    const {
        isListening,
        transcript,
        interimTranscript,
        error: sttError,
        startListening,
        stopListening,
        resetTranscript,
    } = useSpeechToText({ lang: sttLang });

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleStart = async () => {
        setIsRecording(true);
        setIsPaused(false);
        resetTranscript();
        audioChunksRef.current = [];
        KeepDeviceAwake.keepAwake();

        // 1. Web Speech API 시작 (실시간 텍스트) — Android에서는 비활성
        if (!isAndroidNative) startListening();

        // 2. MediaRecorder 시작 (오디오 캡처)
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1, // 모노 강제
                    sampleRate: 48000, // Cloud STT 호환 샘플 속도 요청
                }
            });

            // iOS Safari/WKWebView는 audio/webm 미지원 → mp4 폴백
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/mp4')
                    ? 'audio/mp4'
                    : ''; // 브라우저 기본값
            const recorderOptions: MediaRecorderOptions = mimeType ? { mimeType } : {};
            const recorder = new MediaRecorder(stream, recorderOptions);
            audioMimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm';
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            recorder.start(1000); // 1초 단위 청크
        } catch (err: any) {
            if (err?.name === 'NotAllowedError') {
                console.info('[MediaRecorder] 마이크 권한이 허용되지 않았습니다.');
            } else {
                console.warn('[MediaRecorder] Failed to start:', err);
            }
        }

        const interval = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
        setTimerInterval(interval);
    };

    const handlePause = () => {
        setIsRecording(false);
        setIsPaused(true);
        setSavedTranscript(transcript);
        if (!isAndroidNative) stopListening();
        KeepDeviceAwake.allowSleep();

        // MediaRecorder 일시정지 (스트림 유지)
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.pause();
        }

        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
    };

    const handleResume = () => {
        setIsRecording(true);
        setIsPaused(false);
        KeepDeviceAwake.keepAwake();

        // Web Speech API 재개 — Android에서는 비활성
        if (!isAndroidNative) startListening();

        // MediaRecorder 재개
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
            mediaRecorderRef.current.resume();
        }

        const interval = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
        setTimerInterval(interval);
    };

    const handleFinalStop = () => {
        setIsRecording(false);
        setIsPaused(false);
        setSavedTranscript(transcript);
        if (!isAndroidNative) stopListening();
        KeepDeviceAwake.allowSleep();

        // MediaRecorder 완전 중지 + 스트림 해제
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
        }

        if (timerInterval) {
            clearInterval(timerInterval);
            setTimerInterval(null);
        }
    };

    const handleSave = async () => {
        if (isRecording || isPaused) handleFinalStop();

        const hasAudio = audioChunksRef.current.length > 0;

        // 녹음 안 했으면 차단
        if (!hasAudio) {
            alert(t`No recorded audio. Please check microphone permissions and try again.`);
            return;
        }

        setIsSaving(true);
        try {
            // roomId from props
            const userId = My.user?.id;
            if (!roomId || !userId) throw new Error('User not logged in');

            // Web Speech API 텍스트 (실시간 모니터링용 — 최종 결과에는 미사용)
            const realtimeText = (transcript || savedTranscript).trim();

            // 1. Firestore에 회의록 문서 생성 (처리 중 상태)
            const minutesId = await Fire.aiMemo.saveMinutes(roomId, userId, {
                title: '',
                transcript: realtimeText || t`(Processing high-precision speech recognition...)`,
                tags: [],
                language: sttLang,
                duration: elapsedTime,
                status: 'processing',
                sttStatus: 'processing',
            });

            setSavedMinutesId(minutesId);

            // 2. 오디오 업로드 → Cloud STT → AI 분석 (순차 파이프라인)
            if (hasAudio) {
                uploadTranscribeAndAnalyze(roomId, minutesId, sttLang).catch(err =>
                    console.warn('[Pipeline] Failed:', err)
                );
            }
        } catch (e) {
            console.error('Failed to save minutes:', e);
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * 단일 파이프라인: 오디오 업로드 → Cloud STT → AI 회의록 생성
     */
    const uploadTranscribeAndAnalyze = async (roomId: string, minutesId: string, language: string) => {
        // ── Step 1: 오디오 업로드 ──
        setSttUpgradeStatus('uploading');
        const actualMime = audioMimeTypeRef.current;
        const ext = actualMime.includes('mp4') ? 'mp4' : 'webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: actualMime });
        const storagePath = `ai-minutes/${roomId}/${minutesId}/recording.${ext}`;

        try {
            const storage = getStorage();
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, audioBlob);
        } catch (err) {
            console.error('[Pipeline] Audio upload failed:', err);
            Sentry.captureException(err, {
                tags: { feature: 'ai-minutes', step: 'audio-upload' },
                extra: { roomId, minutesId, mimeType: actualMime, blobSize: audioBlob.size },
            });
            setSttUpgradeStatus('upload-error');
            await Fire.aiMemo.updateMinutes(roomId, minutesId, { sttStatus: 'failed', sttError: (err as Error).message });
            return;
        }

        // ── Step 2: Cloud STT (타임스탬프) ──
        setSttUpgradeStatus('processing');
        try {
            const transcribeAudio = getFunc('transcribeAudio');
            const sttResult = await transcribeAudio({
                roomId,
                minutesId,
                storagePath,
                language,
                enableDiarization: true,
            });

            const sttTranscript = (sttResult?.data as any)?.transcript || '';

            setSttUpgradeStatus('done');

            if (!sttTranscript) {
                console.warn('[Pipeline] No transcript from STT');
                return;
            }

            // ── Step 3: AI 회의록 생성 (클라이언트 — firebase/ai) ──
            lastSttTranscriptRef.current = sttTranscript;
            await runAiAnalysis(roomId, minutesId, sttTranscript, language, elapsedTime);
        } catch (err) {
            console.error('[Pipeline] Cloud STT failed:', err);
            Sentry.captureException(err, {
                tags: { feature: 'ai-minutes', step: 'cloud-stt' },
                extra: { roomId, minutesId, language },
            });
            setSttUpgradeStatus('stt-error');
            await Fire.aiMemo.updateMinutes(roomId, minutesId, { sttStatus: 'failed', sttError: (err as Error).message });
        }
    };

    /** AI 분석 실행 (재시도 가능) */
    const runAiAnalysis = async (roomId: string, minutesId: string, transcript: string, language: string, dur?: number) => {
        setAiAnalysisStatus('analyzing');
        try {
            const result = await analyzeMinutes(transcript, language, dur);
            const cleanData = JSON.parse(JSON.stringify({
                title: result.title || '',
                icon: result.icon || '📝',
                minutes: result.minutes || {},
                tags: result.tags || [],
                transcript: transcript,
                status: 'completed',
            }));
            await Fire.aiMemo.updateMinutes(roomId, minutesId, cleanData);
            setAiAnalysisStatus('done');

        } catch (aiErr) {
            console.warn('[Pipeline] AI analysis failed:', aiErr);
            Sentry.captureException(aiErr, {
                tags: { feature: 'ai-minutes', step: 'ai-analysis' },
                extra: { roomId, minutesId, language, transcriptLength: transcript.length },
            });
            setAiAnalysisStatus('error');
        }
    };

    return (
        <div className="p-5 space-y-5 overflow-y-auto max-h-[90vh]">
            {/* Language Selector */}
            {!isRecording && !isPaused && (
                <div className="space-y-3">
                    {/* Language Selector */}
                    <div className="flex items-center justify-center gap-2">
                        {LANG_OPTIONS.map(opt => (
                            <button
                                key={opt.code}
                                onClick={() => setSttLang(opt.code)}
                                className={cn(
                                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border border-transparent",
                                    sttLang === opt.code
                                        ? "bg-foreground text-background border-foreground shadow-sm"
                                        : "bg-background text-foreground border-border hover:border-border hover:bg-muted/50"
                                )}
                            >
                                <span>{opt.flag}</span>
                                <span>{opt.label}</span>
                            </button>
                        ))}
                    </div>

                </div>
            )}

            {/* Recording Status — 분석 시작 전에만 표시 */}
            {!savedMinutesId && (
                <div className="flex flex-col items-center gap-6 py-8">
                    {/* Timer */}
                    <div className="flex items-center gap-2 text-3xl font-mono font-bold text-foreground">
                        <Clock className={cn("w-6 h-6", isRecording && "text-destructive animate-pulse")} />
                        {formatTime(elapsedTime)}
                    </div>

                    {/* Mic Button */}
                    {!isRecording && !isPaused ? (
                        <button
                            onClick={handleStart}
                            className="w-28 h-28 rounded-full bg-foreground hover:bg-foreground/90 flex items-center justify-center shadow-xl transition-all hover:scale-105"
                        >
                            <Mic className="w-12 h-12 text-background" />
                        </button>
                    ) : isRecording ? (
                        <button
                            onClick={handlePause}
                            className="w-28 h-28 rounded-full bg-destructive hover:bg-destructive/90 flex items-center justify-center shadow-xl animate-pulse transition-all"
                        >
                            <Pause className="w-10 h-10 text-destructive-foreground" />
                        </button>
                    ) : (
                        <button
                            onClick={handleResume}
                            className="w-28 h-28 rounded-full bg-foreground hover:bg-foreground/90 flex items-center justify-center shadow-xl transition-all hover:scale-105"
                        >
                            <Play className="w-10 h-10 text-background ml-1" />
                        </button>
                    )}

                    {/* Waveform */}
                    <VoiceWaveform isActive={isRecording} className="mt-2" />

                    <p className="text-sm text-muted-foreground">
                        {isRecording
                            ? t`Recording meeting... Press pause to temporarily stop`
                            : isPaused
                                ? t`Paused. Press play to resume, or end the meeting below.`
                                : t`Press the microphone button to start recording`}
                    </p>
                </div>
            )}

            {/* Live Transcript — Android 네이티브에서는 숨김 */}
            {!isAndroidNative && (transcript || interimTranscript) && (
                <div className="p-3 bg-muted/50 rounded-xl border text-sm max-h-48 overflow-y-auto">
                    <p className="text-xs text-muted-foreground font-medium mb-1 flex items-center gap-1">
                        <IconSparkles className="w-3 h-3" />
                        <Trans>Live Transcript</Trans>
                    </p>
                    <p className="whitespace-pre-wrap text-foreground leading-relaxed">
                        {transcript}
                        {interimTranscript && (
                            <span className="text-muted-foreground"> {interimTranscript}</span>
                        )}
                    </p>
                </div>
            )}

            {/* Error */}
            {sttError && (
                <p className="text-sm text-destructive">{sttError}</p>
            )}

            {/* Saved — 진행 상황 표시 */}
            {savedMinutesId && (
                <div className="space-y-3">
                    <div className="p-4 bg-badge-success-bg border border-badge-success/20 rounded-xl text-center">
                        <p className="text-sm font-medium text-badge-success">
                            <Trans>✅ Recording completed. Generating meeting minutes...</Trans>
                        </p>
                    </div>
                    {(sttUpgradeStatus === 'processing' || sttUpgradeStatus === 'done' || sttUpgradeStatus === 'uploading') && (
                        <p className="text-xs text-muted-foreground text-center">
                            <Trans>💡 Processing continues on the server even if you close this screen. AI summary will be generated automatically when you open the minutes later.</Trans>
                        </p>
                    )}

                    {/* Step 1: 오디오 업로드 + Cloud STT */}
                    {sttUpgradeStatus !== 'idle' && (
                        <div className={cn(
                            "p-3 rounded-lg border text-sm flex items-center gap-2",
                            sttUpgradeStatus === 'done' ? "bg-badge-success-bg border-badge-success/20 text-badge-success"
                                : (sttUpgradeStatus === 'upload-error' || sttUpgradeStatus === 'stt-error') ? "bg-destructive/10 border-destructive/20 text-destructive"
                                    : "bg-badge-info-bg border-badge-info/20 text-badge-info"
                        )}>
                            {(sttUpgradeStatus === 'uploading' || sttUpgradeStatus === 'processing') && (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            )}
                            {sttUpgradeStatus === 'uploading' && <Trans>① Uploading audio...</Trans>}
                            {sttUpgradeStatus === 'processing' && <Trans>② High-precision speech recognition in progress...</Trans>}
                            {sttUpgradeStatus === 'done' && <Trans>✅ Speech recognition complete</Trans>}
                            {sttUpgradeStatus === 'upload-error' && (
                                <>
                                    <span><Trans>⚠️ Audio upload failed. Please check your network connection.</Trans></span>
                                    <button
                                        onClick={() => savedMinutesId && uploadTranscribeAndAnalyze(roomId, savedMinutesId, sttLang)}
                                        className="ml-auto text-xs font-semibold text-destructive hover:text-destructive underline shrink-0"
                                    >
                                        <Trans>Retry</Trans>
                                    </button>
                                </>
                            )}
                            {sttUpgradeStatus === 'stt-error' && (
                                <>
                                    <span><Trans>⚠️ Speech recognition failed. The server may be temporarily unavailable.</Trans></span>
                                    <button
                                        onClick={() => savedMinutesId && uploadTranscribeAndAnalyze(roomId, savedMinutesId, sttLang)}
                                        className="ml-auto text-xs font-semibold text-destructive hover:text-destructive underline shrink-0"
                                    >
                                        <Trans>Retry</Trans>
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* Step 2: AI 회의록 생성 */}
                    {aiAnalysisStatus !== 'idle' && (
                        <div className={cn(
                            "p-3 rounded-lg border text-sm flex items-center gap-2",
                            aiAnalysisStatus === 'analyzing' ? "bg-badge-info-bg border-badge-info/20 text-badge-info"
                                : aiAnalysisStatus === 'done' ? "bg-badge-success-bg border-badge-success/20 text-badge-success"
                                    : "bg-destructive/10 border-destructive/20 text-destructive"
                        )}>
                            {aiAnalysisStatus === 'analyzing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            {aiAnalysisStatus === 'analyzing' && <Trans>③ Generating AI meeting minutes...</Trans>}
                            {aiAnalysisStatus === 'done' && <Trans>✅ AI meeting minutes generated</Trans>}
                            {aiAnalysisStatus === 'error' && (
                                <>
                                    <span><Trans>⚠️ AI analysis failed</Trans></span>
                                    <button
                                        onClick={() => {
                                            // roomId from props
                                            if (roomId && savedMinutesId && lastSttTranscriptRef.current) {
                                                runAiAnalysis(roomId, savedMinutesId, lastSttTranscriptRef.current, sttLang, elapsedTime);
                                            }
                                        }}
                                        className="ml-auto text-xs font-semibold text-destructive hover:text-destructive underline"
                                    >
                                        <Trans>Retry</Trans>
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    {/* 액션 버튼 */}
                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => onSaved(savedMinutesId)}
                        >
                            <Trans>Close</Trans>
                        </Button>
                        {onViewMinutes && (
                            <Button
                                className="flex-1"
                                onClick={() => onViewMinutes(savedMinutesId)}
                            >
                                <Trans>View Minutes</Trans>
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Action Buttons — 저장 전에만 표시 */}
            {!savedMinutesId && (
                <div className="flex items-center justify-between pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={onCancel}
                        disabled={isSaving}
                    >
                        <Trans>Cancel</Trans>
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || isRecording || !isPaused}
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                <Trans>AI analyzing...</Trans>
                            </>
                        ) : (
                            <>
                                <IconSparkles className="w-4 h-4 mr-2" />
                                <Trans>Save &amp; AI Analysis</Trans>
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AiMinutesCreate;
